import { Conversation } from 'src/services/whatsapp'
import { format, formatDistanceToNow } from 'date-fns'
import { cn } from 'src/utils/style'
import { FiCalendar } from 'react-icons/fi'
import { formatPhoneNumber } from 'react-phone-number-input'
import { useTranslation } from 'react-i18next'

interface ConversationListItemProps {
  conversation: Conversation
  isSelected: boolean
  onClick: () => void
}

/**
 * Formats an E.164 phone number to a human-readable form.
 * e.g. "+14155552671" -> "+1 (415) 555-2671"
 * Falls back to the raw string if formatting fails.
 */
function formatNumber(e164: string): string {
  if (!e164) return ''
  try {
    const formatted = formatPhoneNumber(e164 as `+${string}`)
    return formatted || e164
  } catch {
    return e164
  }
}

export const ConversationListItem = ({
  conversation,
  isSelected,
  onClick,
}: ConversationListItemProps) => {
  const { t } = useTranslation()
  const guestName = conversation.guestRef
    ? `${conversation.guestRef.firstName || ''} ${conversation.guestRef.lastName || ''}`.trim()
    : ''
  // Prefer a linked contact's name, then a guest name, then the raw number.
  const displayName =
    conversation.contactRef?.displayName || guestName || conversation.contactNumber

  const hasUnread = conversation.unreadClientCount > 0
  const hasReservation = Boolean(conversation.reservationRef)

  const formatTimestamp = (dateString: string | null | undefined) => {
    if (!dateString) return ''
    try {
      const date = new Date(dateString)
      const now = new Date()
      const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60)

      if (diffInHours < 24) {
        return formatDistanceToNow(date, { addSuffix: false })
      }
      return format(date, 'MMM d')
    } catch {
      return ''
    }
  }

  return (
    <div
      onClick={onClick}
      className={cn([
        'flex cursor-pointer items-center gap-3 rounded-lg p-3 transition-colors',
        isSelected ? 'bg-gray-100' : 'hover:bg-surface-hover',
        hasUnread && 'bg-surface-hover',
      ])}
    >
      {/* Avatar placeholder */}
      <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-primary-200 text-primary-800">
        <span className="text-lg font-semibold">{displayName.charAt(0).toUpperCase()}</span>
      </div>

      {/* Contact info */}
      <div className="flex flex-1 flex-col gap-1 overflow-hidden">
        <div className="flex items-center justify-between gap-2">
          <span className={cn(['truncate font-semibold', hasUnread && 'font-bold'])}>
            {displayName}
          </span>
          {conversation.lastMessageAt && (
            <span className="flex-shrink-0 text-xs text-text-placeholder">
              {formatTimestamp(conversation.lastMessageAt)}
            </span>
          )}
        </div>
        <p className="max-w-full truncate text-sm text-text-placeholder">
          {conversation.lastMessagePreview ?? '—'}
        </p>
        {/* WhatsApp line (ourNumber) — always shown so users with multiple lines can tell them apart */}
        <p className="truncate text-xs text-text-tertiary">
          {t('chat.via')} {formatNumber(conversation.ourNumber)}
        </p>
        <div className="flex items-center gap-2">
          {hasReservation && (
            <span className="flex-shrink-0 rounded bg-accent-100 px-2 py-0.5 text-xs font-medium text-accent-800">
              <FiCalendar className="inline h-3 w-3" /> {t('chat.reservation')}
            </span>
          )}
          {hasUnread && (
            <span className="flex-shrink-0 rounded-full bg-primary-500 px-2 py-0.5 text-xs font-bold text-white">
              {conversation.unreadClientCount}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
